// react
import { useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams } from '@tanstack/react-router';

// material ui
import { CircularProgress, Container, Stack } from '@mui/material';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';
import { useGetV2LocationsByIdQuery } from '@/api/generated/recalc/endpoints/v2/locations';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// feature-utils
import { getRootNode } from '@/features/energyxplorer/utils/energyTreeFunctions';
import { DEFAULT_OPENING_HOURS } from '@/features/energyxplorer/utils/openingHoursDefaults';

// components
import EditEnergyGroupForm from '@/features/energyxplorer/forms/EditEnergyGroupForm';
import type { LoadedEnergyGroup } from '@/features/energyxplorer/forms/editEnergyGroupForm.mappers';
import ErrorBoundary from '@/routes/-ErrorBoundary.tsx';

/**
 * Backend may return JSON-encoded strings for complex option fields.
 */
function parseJsonObjectMaybe(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === 'object') return value; // already parsed
  if (typeof value !== 'string') return fallback;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function parseArrayMaybe(value, fallback = []) {
  if (value == null) return fallback;
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return fallback;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

export const Route = createFileRoute(
  '/$accountId/_user/energyxplorer/deepdive/$energyGroupId/edit',
)({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
  staticData: { crumb: 'common:actions.edit', hideEnergyAnalysisAppBar: true },
});

function RouteComponent() {
  const { t } = useTranslation();
  const { energyGroupId } = useParams({ strict: false });
  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );

  const { data: locationData } = useGetV2LocationsByIdQuery(
    { id: selectedEnergyGroup?.locationId },
    { skip: !selectedEnergyGroup?.locationId },
  );

  const rootNode = useMemo(
    () => getRootNode(selectedEnergyGroup?.energyTreeNodes || null),
    [selectedEnergyGroup?.energyTreeNodes],
  );

  const rootNodeId = useMemo(() => {
    return (
      rootNode?.energyTreeNodeId ||
      rootNode?.EnergyTreeNodeId ||
      rootNode?.id ||
      null
    );
  }, [rootNode]);

  const normalizedNodeOptions = useMemo(() => {
    const options = rootNode?.options || {};
    return {
      specialNodeType: options.specialNodeType ?? null,
      percentage: options.percentage ?? 0,
      consumption: options.consumption ?? 0,
      openingHours: parseJsonObjectMaybe(
        options.openingHours,
        DEFAULT_OPENING_HOURS,
      ),
      goShares: parseArrayMaybe(options.goShares, []),
    };
  }, [rootNode?.options]);

  const handleCancel = () => {
    window.history.back();
  };

  if (
    !selectedEnergyGroup?.energyGroupId ||
    selectedEnergyGroup.locationId == null ||
    !selectedEnergyGroup.type ||
    !locationData
  ) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  // Use a key to remount the form when switching energy group, avoiding setState in effects.
  const formKey = selectedEnergyGroup.energyGroupId;

  return (
    <PageContainer maxWidth="md">
      <PageHeader title={t('common:actions.edit')} />
      <EditEnergyGroupForm
        key={formKey}
        energyGroup={selectedEnergyGroup as LoadedEnergyGroup}
        location={locationData}
        normalizedNodeOptions={normalizedNodeOptions}
        rootNodeId={rootNodeId}
        onCancel={handleCancel}
      />
    </PageContainer>
  );
}
